import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { getUserDailyWellnessRef } from '@shared/services/userContentService';
import { useLiveDataUid } from './useLiveDataUid';
import { logError } from '@/lib/log';

/**
 * Focused slice behind the DataContext facade: the daily wellness message
 * from the userContent dailyWellness subcollection.
 *
 * NOTE: components should keep consuming useData() from DataContext for now —
 * demo mode (DemoProviders) only stubs the facade's DataContext.Provider, so a
 * component reading this context directly would crash under /demo.
 */
interface DailyWellnessContextType {
    dailyWellnessMessage: string;
}

export const DailyWellnessContext = createContext<DailyWellnessContextType | undefined>(undefined);

export const useDailyWellnessContext = () => {
    const context = useContext(DailyWellnessContext);
    if (!context) {
        throw new Error('useDailyWellnessContext must be used within a DailyWellnessProvider');
    }
    return context;
};

export const DailyWellnessProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const uid = useLiveDataUid();
    const [dailyWellnessMessage, setDailyWellnessMessage] = useState<string>("Loading your calm...");

    useEffect(() => {
        if (!uid) {
            setDailyWellnessMessage("Breathe. You are taking control.");
            return;
        }

        // Fetch daily wellness message from userContent subcollection
        const dailyMessageRef = getUserDailyWellnessRef(uid);
        return onSnapshot(dailyMessageRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.data();
                const message = data?.message || "Breathe. You are taking control.";
                setDailyWellnessMessage(message);
            } else {
                setDailyWellnessMessage("Breathe. You are taking control.");
            }
        }, (error) => {
            logError(error, { context: 'fetch_daily_wellness_message' });
            setDailyWellnessMessage("Breathe. You are taking control.");
        });
    }, [uid]);

    const value = useMemo(() => ({ dailyWellnessMessage }), [dailyWellnessMessage]);

    return (
        <DailyWellnessContext.Provider value={value}>
            {children}
        </DailyWellnessContext.Provider>
    );
};
